import { Component } from '@theme/component';
import { firePendingSubmissionIfAny, stashPendingSubmission } from './oc-inquiry-measurement-v2.js';

/** sessionStorage/Klaviyo key for this form family. */
const FORM_KEY = 'general-inquiry';

/**
 * @typedef {object} GeneralInquiryFormRefs
 * @property {HTMLSelectElement} [topicInput]
 * @property {HTMLTextAreaElement} [messageInput]
 * @property {HTMLInputElement} [firstNameInput]
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLInputElement} [phoneInput]
 * @property {HTMLInputElement} [marketingInput]
 * @property {HTMLTextAreaElement} [bodyInput]
 * @property {HTMLButtonElement} [submitButton]
 *
 * @extends {Component<GeneralInquiryFormRefs>}
 */
class GeneralInquiryFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const preselect = this.dataset.preselectTopic;
    const { topicInput } = this.refs;

    if (preselect && topicInput) {
      const hasOption = Array.from(topicInput.options).some((option) => option.value === preselect);
      if (hasOption) topicInput.value = preselect;
    }

    // `.quote-form__success` only renders when Liquid's `form.posted_successfully?`
    // is true, so its presence is a reliable success signal after the page reload.
    firePendingSubmissionIfAny(FORM_KEY, Boolean(this.querySelector('.quote-form__success')));
  }

  /**
   * Composes the hidden `contact[body]` field from the individual inputs right
   * before the native form submits, since Shopify's contact endpoint only
   * delivers `name`, `email`, `phone` and `body`. Also stashes the submission
   * for the Klaviyo data push, picked up after the page reloads on success.
   */
  handleSubmit() {
    const { bodyInput, submitButton } = this.refs;

    if (bodyInput) {
      bodyInput.value = this.#composeBody();
    }

    this.#stashKlaviyoSubmission();

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }
  }

  #stashKlaviyoSubmission() {
    const { topicInput, messageInput, marketingInput, firstNameInput, emailInput, phoneInput } = this.refs;

    stashPendingSubmission(FORM_KEY, {
      email: emailInput?.value,
      firstName: firstNameInput?.value,
      phone: phoneInput?.value,
      sourceLabel: this.dataset.sourceLabel,
      family: 'General Inquiry',
      properties: {
        Topic: topicInput?.value,
        Message: messageInput?.value,
        'Marketing consent': Boolean(marketingInput?.checked),
      },
    });
  }

  /**
   * @returns {string}
   */
  #composeBody() {
    const { topicInput, messageInput } = this.refs;
    const sourceLabel = this.dataset.sourceLabel;

    /** @type {[string, string | undefined][]} */
    const rows = [
      ['Source', sourceLabel],
      ['Topic', topicInput?.value],
      ['Message', messageInput?.value],
    ];

    return rows
      .filter(([, value]) => value)
      .map(([label, value]) => `${label}: ${value}`)
      .join('\n');
  }
}

if (!customElements.get('general-inquiry-form-component')) {
  customElements.define('general-inquiry-form-component', GeneralInquiryFormComponent);
}
